import { createAction, props } from '@ngrx/store';
import { AppStateFailure } from '@symphony-solo/shared/common-state/src';
import {
  CustomerInfo,
  Discount,
  GuestCheckoutDiscount,
  Order,
  OrderDetails,
  OrderItem,
  OrderLocation,
  PaymentSessionCreate,
  SessionCreate,
  TimeSlot,
} from '../models';
import { PaymentSessionRetrieve } from '../models/paymentRetrieve.model';
import { ChildDeliveryLocation, DeliveryLocationData, Table } from '@symphony-solo/domain/choose-location/src';

export enum OrderActionTypes {
  GetOrderAction = '[Order] GetOrderAction',
  GetOrderSuccessAction = '[Order] GetOrderSuccess',
  GetOrderErrorAction = '[Order] GetOrderError',
  GetOrderDetailsAction = '[Order] GetOrderDetailsAction',
  GetOrderDetailsSuccessAction = '[Order] GetOrderDetailsSuccess',
  GetOrderDetailsErrorAction = '[Order] GetOrderDetailsError',
  VerifyOrderAction = '[Order] VerifyOrderAction',
  VerifyOrderSuccessAction = '[Order] VerifyOrderSuccessAction',
  VerifyOrderErrorAction = '[Order] VerifyOrderErrorAction',
  DiscountAction = '[Order] DiscountAction',
  DiscountSuccessAction = '[Order] DiscountSuccessAction',
  DiscountErrorAction = '[Order] DiscountErrorAction',
  SessionCreateAction = '[Order] SessionCreateAction',
  SessionCreateSuccessAction = '[Order] SessionCreateSuccessAction',
  SessionCreateErrorAction = '[Order] SessionCreateErrorAction',
  SessionRetrieveAction = '[Order] SessionRetrieveAction',
  SessionRetrieveSuccessAction = '[Order] SessionRetrieveSuccessAction',
  SessionRetrieveErrorAction = '[Order] SessionRetrieveErrorAction',
  OrderCompleteAction = '[Order] OrderCompleteAction',
  OrderCompleteSuccessAction = '[Order] OrderCompleteSuccessAction',
  OrderCompleteErrorAction = '[Order] OrderCompleteErrorAction',
  NoFailureAction = '[Order] NoFailureAction',
  ClearStateAction = '[Order] ClearStateAction',
  SetPeriodId = '[Order] SetPeriodId',
  SetOrderLocation = '[Order] SetOrderLocation',
  SetAccountID = '[Order] SetAccountID',
  SetOrderID = '[Order] SetOrderID',
  SetTimeSlot = '[Order] SetTimeSlot',
  SetDeliveryLocation = '[Order] SetDeliveryLocation',
  SetChildDeliveryLocation = '[Order] SetChildDeliveryLocation',
  SetTable = '[Order] SetTable',
  SetOrderCompletedOn = '[Order] SetOrderCompletedOn',
  ShowGuestCheckoutAlertAction = '[Order] ShowGuestCheckoutAlertAction',
  GuestCheckoutDiscountSuccessAction = '[Order] GuestCheckoutDiscountSuccessAction',
  GuestCheckoutDiscountErrorAction = '[Order] GuestCheckoutDiscountErrorAction',
  SessionCreateGuestCheckoutAction = '[Order] SessionCreateGuestCheckoutAction',
  SessionCreateGuestCheckoutErrorAction = '[Order] SessionCreateGuestCheckoutErrorAction',
  SessionRetrieveGuestCheckoutAction = '[Order] SessionRetrieveGuestCheckoutAction',
  SetCustomerInfo = '[Order] SetCustomerInfo',
  AddOrderItem = '[Order] AddOrderItem',
  AddOrderItemSuccess = '[Order] AddOrderItemSuccess',
  AddOrderItemError = '[Order] AddOrderItemError',
  SetReOrderItems = '[Order] SetReOrderItems',
  AutoUpdateTimeSlotIfExpiredAction = '[Order] AutoUpdateTimeSlotIfExpiredAction',
  CompletePaymentInProcess = '[Order] CompletePaymentInProcess',
  CheckOrderStatusAction = '[Order] CheckOrderStatusAction',
  CheckTopUpStatusAction = '[Order] CheckTopUpStatusAction',
  SetOrderNotes = '[Order] SetOrderNotes',
}

export const getOrderAction = createAction(OrderActionTypes.GetOrderAction);
export const getOrderSuccessAction = createAction(OrderActionTypes.GetOrderSuccessAction, props<{ order: Order[] }>());
export const getOrderErrorAction = createAction(OrderActionTypes.GetOrderErrorAction, props<{ failure: AppStateFailure }>());

export const OrderDetailsAction = createAction(OrderActionTypes.GetOrderDetailsAction, props<{ id: number }>());
export const OrderDetailsSuccessAction = createAction(
  OrderActionTypes.GetOrderDetailsSuccessAction,
  props<{ orderDetails: OrderDetails }>()
);
export const OrderDetailsErrorAction = createAction(OrderActionTypes.GetOrderDetailsErrorAction, props<{ failure: AppStateFailure }>());

export const VerifyOrderAction = createAction(OrderActionTypes.VerifyOrderAction, props<{ order: Order }>());
export const VerifyOrderSuccessAction = createAction(OrderActionTypes.VerifyOrderSuccessAction);
export const VerifyOrderErrorAction = createAction(OrderActionTypes.VerifyOrderErrorAction, props<{ failure: AppStateFailure }>());

// export const DiscountAction = createAction(OrderActionTypes.DiscountAction, props<DiscountModal>());
export const DiscountAction = createAction(OrderActionTypes.DiscountAction, props<{ createSessionAction?: boolean }>());
export const DiscountSuccessAction = createAction(OrderActionTypes.DiscountSuccessAction, props<{ discountResponse: Discount[] }>());
export const DiscountErrorAction = createAction(OrderActionTypes.DiscountErrorAction, props<{ failure: AppStateFailure }>());

export const SessionCreateAction = createAction(OrderActionTypes.SessionCreateAction, props<{ sessionCreate: SessionCreate }>());
export const SessionCreateSuccessAction = createAction(
  OrderActionTypes.SessionCreateSuccessAction,
  props<{ paymentSessionCreate: PaymentSessionCreate }>()
);
export const SessionCreateErrorAction = createAction(OrderActionTypes.SessionCreateErrorAction, props<{ failure: AppStateFailure }>());
export const SessionCreateGuestCheckoutErrorAction = createAction(
  OrderActionTypes.SessionCreateGuestCheckoutErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const SessionRetrieveAction = createAction(OrderActionTypes.SessionRetrieveAction, props<{ completePaymentInProcess: boolean }>());
export const SessionRetrieveSuccessAction = createAction(
  OrderActionTypes.SessionRetrieveSuccessAction,
  props<{ paymentSessionRetrieve: PaymentSessionRetrieve; completePaymentInProcess?: boolean }>()
);
export const SessionRetrieveErrorAction = createAction(OrderActionTypes.SessionRetrieveErrorAction, props<{ failure: AppStateFailure }>());

export const OrderCompleteAction = createAction(OrderActionTypes.OrderCompleteAction);
export const OrderCompleteSuccessAction = createAction(OrderActionTypes.OrderCompleteSuccessAction, props<{ orderId: number }>());
export const OrderCompleteErrorAction = createAction(OrderActionTypes.OrderCompleteErrorAction, props<{ failure: AppStateFailure }>());

export const NoFailureAction = createAction(OrderActionTypes.NoFailureAction);
export const ClearStateAction = createAction(OrderActionTypes.ClearStateAction, props<{ actionType?: string }>());

export const setPeriodId = createAction(OrderActionTypes.SetPeriodId, props<{ periodId: number }>());
export const setOrderLocation = createAction(OrderActionTypes.SetOrderLocation, props<{ orderLocation: OrderLocation }>());
export const setAccountID = createAction(OrderActionTypes.SetAccountID, props<{ accountID: number }>());
export const setOrdertID = createAction(OrderActionTypes.SetOrderID, props<{ orderID: number }>());
export const setTimeSlot = createAction(OrderActionTypes.SetTimeSlot, props<{ timeSlot: TimeSlot }>());
export const setDeliveryLocation = createAction(OrderActionTypes.SetDeliveryLocation, props<{ deliveryLocation: DeliveryLocationData }>());
export const setChildDeliveryLocation = createAction(
  OrderActionTypes.SetChildDeliveryLocation,
  props<{ childDeliveryLocation: ChildDeliveryLocation }>()
);
export const setTable = createAction(OrderActionTypes.SetTable, props<{ table: Table }>());
export const setOrderCompletedOn = createAction(OrderActionTypes.SetOrderCompletedOn, props<{ orderCompletedOn: string }>());
export const showGuestCheckoutAlert = createAction(OrderActionTypes.ShowGuestCheckoutAlertAction);
export const checkOrderStatusAction = createAction(OrderActionTypes.CheckOrderStatusAction, props<{ id: number }>());
export const checkTopUpStatusAction = createAction(OrderActionTypes.CheckTopUpStatusAction, props<{ id: number }>());

export const GuestCheckoutDiscountSuccessAction = createAction(
  OrderActionTypes.GuestCheckoutDiscountSuccessAction,
  props<{ discountResponse: GuestCheckoutDiscount; createSessionAction?: boolean }>()
);
export const GuestCheckoutDiscountErrorAction = createAction(
  OrderActionTypes.GuestCheckoutDiscountErrorAction,
  props<{ failure: AppStateFailure }>()
);

export const SessionCreateGuestCheckoutAction = createAction(
  OrderActionTypes.SessionCreateGuestCheckoutAction,
  props<{ sessionCreate: SessionCreate }>()
);

export const SessionRetrieveGuestCheckoutAction = createAction(
  OrderActionTypes.SessionRetrieveGuestCheckoutAction,
  props<{ sessionRetrieve: string }>()
);

export const AutoUpdateTimeSlotIfExpiredAction = createAction(
  OrderActionTypes.AutoUpdateTimeSlotIfExpiredAction,
  props<{ reCreateSession: boolean }>()
);

export const setCustomerInfo = createAction(OrderActionTypes.SetCustomerInfo, props<{ customerInfo: CustomerInfo }>());

export const addOrderItemAction = createAction(OrderActionTypes.AddOrderItem, props<{ orderItem: OrderItem; cartItemIndex: number }>());

export const addOrderItemSuccessAction = createAction(OrderActionTypes.AddOrderItemSuccess);

export const addOrderItemErrorAction = createAction(OrderActionTypes.AddOrderItemError, props<{ failure: AppStateFailure }>());

export const setReOrderItems = createAction(OrderActionTypes.SetReOrderItems, props<{ reOrderItems: OrderItem[] }>());

export const completePaymentInProcessAction = createAction(OrderActionTypes.CompletePaymentInProcess);

export const setOrderNotes = createAction(OrderActionTypes.SetOrderNotes, props<{ notes: string }>());
